import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const FORM_URL = 'https://readdy.ai/api/form/d9sr3edlbrt1qcgaj8j0';

const aboutJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于茄子视频',
  description: '茄子视频品牌介绍、发展历程、企业荣誉及联系方式。茄子视频致力于打造国内领先的短视频娱乐平台。',
  url: 'https://www.qiezitv.com/about',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://www.qiezitv.com/', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': 'https://www.qiezitv.com/about', name: '关于我们' } },
    ],
  },
};

const milestones = [
  { year: '2022', title: '创立之初', desc: '茄子视频正式成立，团队怀揣"让每个人都成为创作者"的愿景开始征程。' },
  { year: '2023', title: '用户破千万', desc: '凭借优质内容和流畅体验，注册用户突破1000万，日活用户超过300万。' },
  { year: '2024', title: '创作者生态', desc: '推出创作者激励计划，平台创作者数量突破50万，多个原创IP成功孵化。' },
  { year: '2025', title: '技术革新', desc: '全面升级AI推荐算法和创作工具，视频播放流畅度提升40%，用户停留时长增长60%。' },
  { year: '2026', title: '行业领先', desc: '注册用户突破5000万，日均播放量超10亿，稳居国内短视频平台前列。' },
];

const honors = [
  { icon: 'ri-award-line', title: '2025最佳用户体验奖', desc: '中国互联网协会评选' },
  { icon: 'ri-shield-star-line', title: '国家高新技术企业', desc: '科技部认证' },
  { icon: 'ri-medal-line', title: '2024年度创新平台', desc: '中国短视频行业峰会' },
  { icon: 'ri-star-smile-line', title: '最受用户喜爱APP', desc: '2025移动应用年度评选' },
  { icon: 'ri-building-line', title: 'AAA级信用企业', desc: '商务部信用评级' },
  { icon: 'ri-global-line', title: '最佳出海应用', desc: '2026全球移动互联网大会' },
];

export default function AboutPage() {
  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />

            {/* Hero */}
            <div className="text-center py-10 md:py-16">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900">关于茄子视频</h1>
              <p className="mt-4 text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                茄子视频成立于2022年，是国内领先的短视频娱乐平台。我们致力于通过技术创新和内容生态建设，让每一个人都能轻松记录、分享和发现生活中的精彩瞬间。
              </p>
            </div>

            {/* Story Image */}
            <div className="relative w-full h-[300px] md:h-[400px] rounded-lg overflow-hidden mb-16">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20tech%20company%20office%20with%20creative%20team%20collaborating%20around%20large%20screen%2C%20warm%20lighting%2C%20open%20workspace%20design%2C%20energetic%20and%20innovative%20atmosphere%2C%20professional%20corporate%20photography%2C%20clean%20modern%20aesthetic&width=1200&height=500&seq=qiezi-about-hero&orientation=landscape"
                alt="茄子视频团队"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Brand Introduction */}
            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
                品牌故事
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-bold text-foreground-900 mb-3">我们的使命</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    在信息爆炸的时代，茄子视频希望为每个人提供一个简单、有趣、温暖的表达空间。我们相信，每个人都有值得被看见的精彩，每个生活瞬间都值得被记录和分享。通过技术降低创作门槛，让创作不再是少数人的特权，而是每个人都可以享受的乐趣。
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground-900 mb-3">我们的愿景</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    成为全球最受欢迎的短视频社区平台，连接每一位热爱生活的创作者和观众。我们希望通过持续的产品创新和内容生态建设，打造一个充满活力、积极向上的数字娱乐空间，让更多人在这里找到归属感和快乐。
                  </p>
                </div>
              </div>
            </section>

            {/* Milestones */}
            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-primary-200 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {milestones.map((item, idx) => (
                    <div key={idx} className={`relative flex items-start gap-6 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="hidden md:block flex-1"></div>
                      <div className="relative z-10 w-8 h-8 rounded-full bg-primary-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">
                        {idx + 1}
                      </div>
                      <div className="flex-1 bg-background-100 rounded-lg p-4 md:p-5">
                        <span className="text-xs text-primary-500 font-bold">{item.year}</span>
                        <h3 className="text-sm font-bold text-foreground-900 mt-1">{item.title}</h3>
                        <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Honors */}
            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
                企业荣誉
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {honors.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-5 rounded-lg bg-background-100">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center text-accent-600 flex-shrink-0">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-900">{item.title}</h3>
                      <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
                联系我们
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-900">邮箱地址</h3>
                      <p className="text-sm text-foreground-500">contact@qiezitv.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 flex-shrink-0">
                      <i className="ri-phone-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-900">客服热线</h3>
                      <p className="text-sm text-foreground-500">400-888-8888</p>
                      <p className="text-xs text-foreground-400 mt-0.5">周一至周五 9:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-900">公司地址</h3>
                      <p className="text-sm text-foreground-500">北京市朝阳区茄子大厦A座15层</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-500 flex-shrink-0">
                      <i className="ri-wechat-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-foreground-900">官方微信</h3>
                      <p className="text-sm text-foreground-500">qiezivideo_official</p>
                    </div>
                  </div>
                </div>

                <ContactForm />
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}

function ContactForm() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg('');

    const form = e.currentTarget;
    const formDataObj = new FormData(form);

    const honeypot = formDataObj.get('company_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setSubmitted(true);
      setFormData({ name: '', email: '', message: '' });
      setCharCount(0);
      return;
    }

    try {
      const body = new URLSearchParams();
      body.append('name', formDataObj.get('name') as string);
      body.append('email', formDataObj.get('email') as string);
      body.append('message', formDataObj.get('message') as string);

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      if (res.ok && parsed?.code === 'OK') {
        setSubmitted(true);
        setFormData({ name: '', email: '', message: '' });
        setCharCount(0);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text || '提交失败，请稍后再试';
        setErrorMsg(msg);
      }
    } catch {
      setErrorMsg('网络错误，请检查网络后重试');
    }
  };

  if (submitted) {
    return (
      <div className="bg-accent-100 rounded-lg p-8 flex flex-col items-center justify-center text-center">
        <div className="w-12 h-12 rounded-full bg-accent-500 text-white flex items-center justify-center mb-4">
          <i className="ri-check-line text-xl"></i>
        </div>
        <h3 className="text-base font-bold text-accent-800">发送成功！</h3>
        <p className="text-sm text-accent-600 mt-2">感谢您的留言，我们会尽快与您联系。</p>
        <button
          onClick={() => setSubmitted(false)}
          className="mt-4 text-sm text-accent-700 underline cursor-pointer"
        >
          继续留言
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      className="bg-background-100 rounded-lg p-6 space-y-4"
    >
      <div>
        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">姓名</label>
        <input
          id="contact-name"
          type="text"
          name="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">邮箱</label>
        <input
          id="contact-email"
          type="email"
          name="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400"
          placeholder="请输入您的邮箱地址"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">
          留言内容
          <span className="text-foreground-400 font-normal ml-2">({charCount}/500)</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          value={formData.message}
          onChange={(e) => {
            const val = e.target.value;
            if (val.length <= 500) {
              setFormData({ ...formData, message: val });
              setCharCount(val.length);
            }
          }}
          required
          rows={4}
          className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 resize-none"
          placeholder="请输入您想咨询的内容..."
        ></textarea>
      </div>
      <input
        type="text"
        name="company_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="hp-field"
      />
      {errorMsg && (
        <p className="text-xs text-primary-600">{errorMsg}</p>
      )}
      <button
        type="submit"
        className="w-full bg-primary-500 text-white py-2.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
      >
        发送留言
      </button>
    </form>
  );
}